import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react'
import React, { useContext, useState, useEffect, useMemo } from 'react'
import { useMutation, useQueryClient, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import currencySymbolMap from 'currency-symbol-map'

import { AuthContext } from '../../hooks/auth/useAuth'
import handleToast from '../HandleToast'
import ModalSelect from '../forms/modalForms/modalSelect'
import { modal } from '../../themes/styles'

import { type BenefitFrequency, type BenefitType, createBenefit, updateBenefit, fetchEmployeeList, fetchTeamList, fetchEmployee } from '../../api/workforce/workforce'
import { phoneNumbers, countrySelectOptions } from '../countries/PhoneDial'
// ─── Constants ───────────────────────────────────────────────────────────────

type AssignType = 'employee' | 'team' | 'country'

const BENEFIT_TYPE_OPTIONS: { id: BenefitType; name: string }[] = [
  { id: 'pension', name: 'Pension' },
  { id: 'health', name: 'Health' },
  { id: 'education', name: 'Education' },
  { id: 'vehicle', name: 'Vehicle' },
  { id: 'travel_allowance', name: 'Travel allowance' },
  { id: 'extra_month_salary', name: 'Extra month salary' },
  { id: 'vacation_allowance', name: 'Vacation allowance' },
  { id: 'other', name: 'Other' },
]

const BENEFIT_FREQUENCY_OPTIONS: { id: BenefitFrequency; name: string }[] = [
  { id: 'single', name: 'Single' },
  { id: 'monthly', name: 'Monthly' },
  { id: 'annual', name: 'Annual' },
]

// ─── Input styles ─────────────────────────────────────────────────────────────

const inputStyles = {
  size: 'md' as const,
  borderRadius: '12px',
  border: '1.5px solid',
  borderColor: 'solid.100',
  bg: 'solid.0',
  fontSize: '14px',
  h: '44px',
  _placeholder: { color: 'solid.400', fontSize: '14px', fontWeight: 400 },
  _focus: { borderColor: 'primary.100', boxShadow: '0 0 0 3px var(--chakra-colors-primary-50)' },
  _focusVisible: { borderColor: 'primary.100', boxShadow: '0 0 0 3px var(--chakra-colors-primary-50)' },
  _active: { bg: 'solid.0', borderColor: 'primary.100' },
  _hover: { borderColor: 'solid.200' },
  transition: 'all 0.2s',
}

// ─── PillToggle — matches BookRoomModal duration boxes exactly ────────────────

interface PillOption<T extends string> {
  id: T
  name: string
}

interface PillToggleGroupProps<T extends string> {
  options: PillOption<T>[]
  value: T | ''
  onChange: (val: T) => void
}

function PillToggleGroup<T extends string>({ options, value, onChange }: PillToggleGroupProps<T>) {
  return (
    <HStack spacing="8px" flexWrap="wrap">
      {options.map((opt) => {
        const selected = value === opt.id
        return (
          <Box
            key={opt.id}
            as="button"
            type="button"
            py="10px"
            px="16px"
            borderRadius="12px"
            border="1.5px solid"
            borderColor={selected ? 'primary.100' : 'solid.100'}
            bg={selected ? 'primary.0' : 'white'}
            color={selected ? 'primary.200' : 'solid.600'}
            fontWeight={selected ? '600' : '500'}
            fontSize="13px"
            transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
            _hover={{ 
              borderColor: 'primary.100', 
              bg: selected ? 'primary.0' : 'primary.0',
              transform: 'translateY(-1px)',
              boxShadow: 'sm'
            }}
            _active={{ transform: 'translateY(0)' }}
            onClick={() => onChange(opt.id)}
          >
            {opt.name}
          </Box>
        )
      })}
    </HStack>
  )
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AddBenefitModalProps {
  isOpen: boolean
  onClose: () => void
  employeeId?: number
  benefit?: any | null
  readOnly?: boolean
}

interface BenefitFormState {
  type: BenefitType | ''
  frequency: BenefitFrequency | ''
  description: string
  value: string
  benefit_date: string
  assignType: AssignType
  employee?: string | number | null
  team?: string | number | null
  country_of_work?: string | null
  /** Display name for selected employee (so select shows it when not in current page) */
  employeeName?: string
  /** Display name for selected team (so select shows it when not in current page) */
  teamName?: string
}

const EMPTY_FORM: BenefitFormState = {
  type: '',
  frequency: '',
  description: '',
  value: '',
  benefit_date: '',
  assignType: 'employee',
  employee: undefined,
  team: undefined,
  country_of_work: undefined,
}

// ─── Component ────────────────────────────────────────────────────────────────

const AddBenefitModal: React.FC<AddBenefitModalProps> = ({
  isOpen,
  onClose,
  employeeId,
  benefit,
  readOnly = false,
}) => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const isEditMode = !!benefit
  const auth = useContext(AuthContext)
  const userId = auth?.user?.id

  // Fetch logged-in user's currency via GET /workforce/{userId}/?fields=id,currency
  const { data: meData } = useQuery({
    queryKey: ['workforce-currency', userId],
    queryFn: () => fetchEmployee(String(userId), { fields: 'id,currency' }),
    select: (data) => ({ currency: data?.currency }),
    enabled: !!userId && isOpen,
  })

  const [form, setForm] = useState<BenefitFormState>({
    ...EMPTY_FORM,
    assignType: employeeId ? 'employee' : 'employee',
    employee: employeeId,
  })

  // Employee search
  const [employeeSearch, setEmployeeSearch] = useState('')
  const employeeQuery = useInfiniteQuery({
    queryKey: ['modal-employees', 10, employeeSearch],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({
        search: employeeSearch,
        page: pageParam,
        pageSize: 10,
        filters: {},
        minimal: true,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
    enabled: !employeeId && form.assignType === 'employee' && isOpen,
  })
  const employeeList = useMemo(
    () =>
      (employeeQuery.data?.pages ?? []).flatMap((p: any) =>
        (p.data ?? []).map((e: any) => ({
          id: e.id,
          name: [e.first_name, e.last_name].filter(Boolean).join(' ') || e.work_email || String(e.id),
          profile_picture: e.profile_picture,
        }))
      ),
    [employeeQuery.data]
  )

  // Team search
  const [teamSearch, setTeamSearch] = useState('')
  const teamQuery = useInfiniteQuery({
    queryKey: ['modal-teams', 10, teamSearch],
    queryFn: ({ pageParam }) =>
      fetchTeamList({
        search: teamSearch,
        page: pageParam,
        pageSize: 10,
        filters: {},
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
    enabled: !employeeId && form.assignType === 'team' && isOpen,
  })
  const teamList = useMemo(
    () =>
      (teamQuery.data?.pages ?? []).flatMap((p: any) =>
        Array.isArray(p?.data) ? p.data.map((t: any) => ({ id: t.id, name: t.name })) : []
      ),
    [teamQuery.data]
  )


  useEffect(() => {
    if (benefit) {
      // Determine assign type based on what's set
      let assignType: AssignType = 'employee'
      if (benefit.team) assignType = 'team'
      else if (benefit.country_of_work) assignType = 'country'
      
      setForm({
        type: benefit.type ?? '',
        frequency: benefit.frequency ?? '',
        description: benefit.description ?? '',
        value: String(benefit.value ?? ''),
        benefit_date: benefit.benefit_date ?? '',
        assignType,
        employee: benefit.employee ?? employeeId,
        team: benefit.team,
        country_of_work: benefit.country_of_work,
        employeeName: benefit.employee_name,
        teamName: benefit.team_name,
      })
    } else {
      setForm({ 
        ...EMPTY_FORM, 
        assignType: employeeId ? 'employee' : 'employee',
        employee: employeeId 
      })
    }
  }, [benefit, employeeId])
  const [errors, setErrors] = useState<Partial<Record<keyof BenefitFormState, string>>>({})

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const isValid = () => {
    const next: Partial<Record<keyof BenefitFormState, string>> = {}
    
    // Validate assignment based on type
    if (!employeeId) {
      if (form.assignType === 'employee' && !form.employee) {
        next.employee = 'Employee is required'
      } else if (form.assignType === 'team' && !form.team) {
        next.team = 'Team is required'
      } else if (form.assignType === 'country' && !form.country_of_work) {
        next.country_of_work = 'Country is required'
      }
    }
    
    if (!form.type) next.type = 'Type is required'
    if (!form.frequency) next.frequency = 'Frequency is required'
    if (!form.value || isNaN(Number(form.value)) || Number(form.value) < 0) next.value = 'A valid value is required'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: any = {
        type: form.type,
        frequency: form.frequency,
        description: form.description,
        value: Number(form.value),
        ...(form.benefit_date ? { benefit_date: form.benefit_date } : {}),
      }

      if (isEditMode) {
        return updateBenefit(benefit.id.toString(), payload)
      }

      // Add assignment based on type
      if (employeeId) {
        payload.employee = employeeId
      } else {
        if (form.assignType === 'employee') {
          payload.employee = form.employee
        } else if (form.assignType === 'team') {
          payload.team = form.team
        } else if (form.assignType === 'country') {
          payload.country_of_work = form.country_of_work
        }
      }

      return createBenefit(payload)
    },
    onSuccess: () => {
      handleToast(toast, true, 'Success', isEditMode ? 'Benefit updated successfully' : 'Benefit created successfully')

      queryClient.invalidateQueries({ queryKey: ['benefits'] })

      handleClose()
    },
  })

  const handleSubmit = () => {
    if (!isValid()) return
    saveMutation.mutate()
  }

  const handleClose = () => {
    setForm({ 
      ...EMPTY_FORM, 
      assignType: employeeId ? 'employee' : 'employee',
      employee: employeeId 
    })
    setErrors({})
    setEmployeeSearch('')
    setTeamSearch('')
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered closeOnOverlayClick={false} size="xl">
      <ModalOverlay />
      <ModalContent maxW="600px">
        <ModalHeader {...modal.header}>
          {isEditMode ? 'Edit Benefit' : 'Add Benefit'}
        </ModalHeader>
        <ModalCloseButton top="16px" right="20px" sx={modal.closeButton} />

        <ModalBody px="24px" py="20px">
          <VStack spacing="20px" align="stretch">
            {/* Assign To — pill toggles (only show if employeeId not provided) */}
            {!employeeId && (
              <Box w="100%">
                <FormControl>
                  <FormLabel textStyle="l-xs" color="solid.700" mb="8px">
                    Assign To
                  </FormLabel>
                  <PillToggleGroup<AssignType>
                    options={[
                      { id: 'employee', name: 'Employee' },
                      { id: 'team', name: 'Team' },
                      { id: 'country', name: 'Country' },
                    ]}
                    value={form.assignType}
                    onChange={(val) => setForm((prev) => ({ 
                      ...prev, 
                      assignType: val,
                      employee: val === 'employee' ? prev.employee : undefined,
                      team: val === 'team' ? prev.team : undefined,
                      country_of_work: val === 'country' ? prev.country_of_work : undefined,
                    }))}
                  />
                </FormControl>
              </Box>
            )}

            {/* Employee selector — conditional on assignType */}
            {!employeeId && form.assignType === 'employee' && (() => {
              const baseOptions = employeeList.map(e => ({
                value: String(e.id),
                label: e.name,
                profilePicture: e.profile_picture,
              }))
              
              const employeeOptions = form.employee && form.employeeName && !baseOptions.some(o => o.value === String(form.employee))
                ? [{ value: String(form.employee), label: form.employeeName, profilePicture: undefined }, ...baseOptions]
                : baseOptions
              
              return (
                <Box w="100%">
                  <ModalSelect
                    label="Employee"
                    name="employee"
                    value={form.employee ? String(form.employee) : ''}
                    options={employeeOptions}
                    onChange={(e) => {
                      const selectedId = e.target.value
                      if (!selectedId) {
                        setForm((prev) => ({ ...prev, employee: undefined, employeeName: undefined }))
                        return
                      }
                      
                      const selectedOption = employeeOptions.find(opt => opt.value === selectedId)
                      setForm((prev) => ({ ...prev, employee: selectedId, employeeName: selectedOption?.label }))
                    }}
                    placeholder="Select employee"
                    hasProfilePicture
                    isDisabled={readOnly || isEditMode}
                    isInvalid={!!errors.employee}
                    onSearch={setEmployeeSearch}
                    onLoadMore={() => {
                      if (employeeQuery.hasNextPage && !employeeQuery.isFetchingNextPage) {
                        employeeQuery.fetchNextPage()
                      }
                    }}
                    hasMore={!!employeeQuery.hasNextPage}
                    isLoadingMore={employeeQuery.isFetchingNextPage}
                  />
                  {errors.employee && (
                    <Text fontSize="12px" color="error.100" mt="4px">
                      {errors.employee}
                    </Text>
                  )}
                </Box>
              )
            })()}

            {/* Team selector — conditional on assignType */}
            {!employeeId && form.assignType === 'team' && (() => {
              const baseOptions = teamList.map(t => ({ 
                value: String(t.id), 
                label: t.name 
              }))
              
              const teamOptions = form.team && form.teamName && !baseOptions.some(o => o.value === String(form.team))
                ? [{ value: String(form.team), label: form.teamName }, ...baseOptions]
                : baseOptions
              
              return (
                <Box w="100%">
                  <ModalSelect
                    label="Team"
                    name="team"
                    value={form.team ? String(form.team) : ''}
                    options={teamOptions}
                    onChange={(e) => {
                      const selectedId = e.target.value
                      if (!selectedId) {
                        setForm((prev) => ({ ...prev, team: undefined, teamName: undefined }))
                        return
                      }
                      
                      const selectedOption = teamOptions.find(opt => opt.value === selectedId)
                      setForm((prev) => ({ ...prev, team: selectedId, teamName: selectedOption?.label }))
                    }}
                    placeholder="Select team"
                    isDisabled={readOnly || isEditMode}
                    isInvalid={!!errors.team}
                    onSearch={setTeamSearch}
                    onLoadMore={() => {
                      if (teamQuery.hasNextPage && !teamQuery.isFetchingNextPage) {
                        teamQuery.fetchNextPage()
                      }
                    }}
                    hasMore={!!teamQuery.hasNextPage}
                    isLoadingMore={teamQuery.isFetchingNextPage}
                  />
                  {errors.team && (
                    <Text fontSize="12px" color="error.100" mt="4px">
                      {errors.team}
                    </Text>
                  )}
                </Box>
              )
            })()}

            {/* Country selector — conditional on assignType */}
            {!employeeId && form.assignType === 'country' && (
              <Box w="100%">
                <ModalSelect
                  label="Country"
                  name="country_of_work"
                  value={form.country_of_work || ''}
                  options={countrySelectOptions}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    country_of_work: e.target.value || undefined
                  }))}
                  showCountryFlag
                  placeholder="Select country"
                  isDisabled={readOnly || isEditMode}
                  isInvalid={!!errors.country_of_work}
                />
                {errors.country_of_work && (
                  <Text fontSize="12px" color="error.100" mt="4px">
                    {errors.country_of_work}
                  </Text>
                )}
              </Box>
            )}

            {/* Type — dropdown */}
            <Box w="100%">
              <ModalSelect
                label="Type"
                name="type"
                value={form.type}
                options={BENEFIT_TYPE_OPTIONS.map(o => ({
                  value: o.id,
                  label: o.name,
                }))}
                onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value as BenefitType }))}
                placeholder="Select type"
                isDisabled={readOnly}
                isInvalid={!!errors.type}
                searchDisabled
              />
              {errors.type && (
                <Text fontSize="12px" color="error.100" mt="4px">
                  {errors.type}
                </Text>
              )}
            </Box>

            {/* Frequency — pill toggles */}
            <Box w="100%">
              <FormControl>
                <FormLabel textStyle="l-xs" color="solid.700" mb="8px">
                  Frequency
                </FormLabel>
                <PillToggleGroup<BenefitFrequency>
                  options={BENEFIT_FREQUENCY_OPTIONS}
                  value={form.frequency}
                  onChange={(val) => setForm((prev) => ({ ...prev, frequency: val }))}
                />
                {errors.frequency && (
                  <Text fontSize="12px" color="error.100" mt="4px">
                    {errors.frequency}
                  </Text>
                )}
              </FormControl>
            </Box>

            {/* Value (currency will be set by backend based on logged-in user) */}
            <Box w="100%">
              <FormControl>
                <FormLabel textStyle="l-xs" color="solid.700" mb="8px">
                  Value
                  {meData?.currency && (
                    <Text as="span" fontWeight={500} color="primary.200" ml="6px" fontSize="12px">
                      ({meData.currency} {currencySymbolMap(meData.currency) ?? ''})
                    </Text>
                  )}
                </FormLabel>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    h="44px"
                    color="solid.600"
                    fontSize="14px"
                    fontWeight="600"
                  >
                    {meData?.currency ? (currencySymbolMap(meData.currency) ?? meData.currency) : '—'}
                  </InputLeftElement>
                  <Input
                    name="value"
                    type="number"
                    min={0}
                    placeholder="Enter amount"
                    value={form.value}
                    onChange={handleChange}
                    pl="40px"
                    {...inputStyles}
                  />
                </InputGroup>
                {errors.value && (
                  <Text fontSize="12px" color="error.100" mt="4px">
                    {errors.value}
                  </Text>
                )}
              </FormControl>
            </Box>

            {/* Date */}
            <Box w="100%">
              <FormControl>
                <FormLabel textStyle="l-xs" color="solid.700" mb="8px">
                  Date
                  <Text as="span" fontWeight={400} color="solid.400" ml="6px" fontSize="12px">
                    (optional)
                  </Text>
                </FormLabel>
                <Input
                  name="benefit_date"
                  type="date"
                  value={form.benefit_date}
                  onChange={handleChange}
                  {...inputStyles}
                />
              </FormControl>
            </Box>

            {/* Description */}
            <Box w="100%">
              <FormControl>
                <FormLabel textStyle="l-xs" color="solid.700" mb="8px">
                  Description
                  <Text as="span" fontWeight={400} color="solid.400" ml="6px" fontSize="12px">
                    (optional)
                  </Text>
                </FormLabel>
                <Textarea
                  name="description"
                  placeholder="Add a description..."
                  value={form.description}
                  onChange={handleChange}
                  borderRadius="12px"
                  border="1.5px solid"
                  borderColor="solid.100"
                  bg="white"
                  fontSize="14px"
                  resize="none"
                  rows={3}
                  minH="100px"
                  _placeholder={{ color: 'solid.400', fontSize: '14px', fontWeight: 400 }}
                  _focus={{ borderColor: 'primary.100', boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)' }}
                  _focusVisible={{ borderColor: 'primary.100', boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)' }}
                  _hover={{ borderColor: 'solid.200' }}
                  transition="all 0.2s"
                />
              </FormControl>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter {...modal.footer}>
          <Button variant="white" w="100%" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="mono" w="100%" onClick={handleSubmit} isLoading={saveMutation.isPending}>
            {isEditMode ? 'Save Changes' : 'Add Benefit'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default AddBenefitModal
